/**
 * Month close pack: one printable page for a sealed month. Every figure comes from the published
 * month, its transactions and category totals. Save as PDF from the browser's print dialog.
 */
import { useApp } from '../context';
import { useAsync } from '../hooks';
import { Empty, ErrorNote, Loading, Panel } from '../ui';
import { loadMw } from '../MigrateWorld';
import { mwOutsideFor } from '../../lib/mw';
import { monthVerdict, monthMoney, reserve, topPayees, RESERVE_TARGET_MONTHS } from '../../lib/decisions';
import { monthKey, previousPublished, publishedMonths } from '../../lib/periods';
import { DASH, dateLabel, money, monthLabel, naira, nairaShort } from '../../lib/format';
import { navigate } from '../../lib/route';
import { balanceAsOf, isInvestmentAccount } from '../../data/types';

const pctOf = (a: number | null, b: number | null) => (a === null || !b ? null : a / b);
const status = (actual: number | null, cap: number | null, higherIsGood = false) => {
  const p = pctOf(actual, cap);
  if (p === null) return { word: DASH, tone: '' };
  const good = higherIsGood ? p >= 1 : p <= 1;
  return { word: higherIsGood ? (good ? 'Met' : 'Short') : (good ? 'Within' : 'Over'), tone: good ? 'within' : 'over' };
};

export function Report({ month: key }: { month?: string }) {
  const { repo, base } = useApp();
  const published = publishedMonths(base.months);
  const month = (key && published.find((m) => monthKey(m.month_start) === key)) || published[0] || null;
  const prior = month ? previousPublished(base.months, month.month_start) : null;
  const state = useAsync(async () => {
    if (!month) return null;
    const [txs, cats, priorCats, mw] = await Promise.all([
      repo.transactions(month.month_start),
      repo.categoryMonths(month.id),
      prior ? repo.categoryMonths(prior.id) : Promise.resolve([]),
      loadMw(repo, base).catch(() => null),
    ]);
    return { txs, cats, priorCats, mw };
  }, [month?.id]);

  if (!month) return <Panel><Empty>A close pack exists once a month is published.</Empty></Panel>;
  if (state.error) return <ErrorNote message={state.error} onRetry={state.reload} />;
  if (!state.data) return <Loading label="Preparing the close pack…" />;

  const { txs, cats, priorCats, mw } = state.data;
  const m = monthMoney(txs, mw ? mwOutsideFor(mw.data.costs, monthKey(month.month_start)) : 0);
  const out = (v: number) => (v > 0 ? `−${naira(v)}` : naira(0));
  const name = monthLabel(month.month_start);
  const mk = monthKey(month.month_start);
  const r = reserve(base.accounts, published.filter((p) => p.month_start <= month.month_start));
  const payees = topPayees(txs, 8);
  const priorBy = new Map(priorCats.map((c) => [c.name, c.actual_amount]));
  const spendCats = cats.filter((c) => ['lifestyle', 'fixed', 'exceptional', 'other'].includes(c.category_type) && (c.actual_amount ?? 0) > 0)
    .sort((a, b) => (b.actual_amount ?? 0) - (a.actual_amount ?? 0));
  const usd = txs.filter((t) => t.currency === 'USD' && t.direction === 'debit');
  const mwMonth = mw?.summary.months.find((x) => x.month === mk) ?? null;
  const mwToDate = mw ? mw.summary.months.filter((x) => x.month <= mk).reduce((a, x) => a + x.ngn, 0) : null;
  const mwReceived = mw ? mw.data.payouts.filter((p) => p.received_on.slice(0, 7) <= mk).reduce((a, p) => a + (p.ngn ?? 0), 0) : null;
  const banks = base.accounts.filter((a) => !isInvestmentAccount(a) && a.current_balance !== null);
  const v = monthVerdict(m);
  const sentence = v.kind === 'unknown' ? `${name} is sealed. No income was recorded, so what was left to move is unknown.`
    : v.kind === 'left' ? `${name} left you ${nairaShort(v.amount)} to move.`
    : v.kind === 'investedBeyond' ? `${name} left ${nairaShort(v.spare)} spare; you invested ${nairaShort(v.invested)}, ${nairaShort(v.fromSavings)} of it from savings.`
    : `${name} spent ${nairaShort(v.amount)} more than it earned.${v.invested > 0 ? ` Separately, ${nairaShort(v.invested)} went into investments.` : ''}`;
  const rows: [string, number | null, number | null, boolean][] = [
    ['Personal spend', month.personal_spend_actual, month.personal_spend_cap, false],
    ['Lifestyle', month.lifestyle_spend_actual, month.lifestyle_spend_cap, false],
    ['Invested', month.investment_actual, month.investment_target, true],
  ];

  return (
    <article className="report">
      <div className="report-actions no-print">
        <button className="secondary" onClick={() => navigate({ section: 'insights', month: mk })}>Back</button>
        <button className="cta" onClick={() => window.print()}>Save as PDF</button>
      </div>

      <header className="report-head">
        <h1>{name} close</h1>
        <p className="report-sealed">Finance Cockpit. Sealed {month.published_at ? dateLabel(month.published_at) : DASH}. Figures from your bank statements; they can no longer change.</p>
        <p className="report-sentence">{sentence}</p>
      </header>

      <section className="report-sec">
        <h2>Where the money went</h2>
        <table className="report-table">
          <tbody>
            <tr><td>Income</td><td>{naira(m.income)}</td></tr>
            <tr><td className="ind">Fixed costs</td><td>{out(m.fixed)}</td></tr>
            <tr><td className="ind">Lifestyle</td><td>{out(m.lifestyle)}</td></tr>
            {m.otherSpend > 0 && <tr><td className="ind">Other personal</td><td>{out(m.otherSpend)}</td></tr>}
            <tr><td className="ind">Migrate World (business){m.businessOutside > 0 && <small>includes {naira(m.businessOutside)} paid outside your accounts</small>}</td><td>{out(m.business)}</td></tr>
            {m.available !== null && <tr className="total"><td>{m.available < 0 ? 'Spent more than earned' : 'Spare after spending'}</td><td>{naira(Math.abs(m.available))}</td></tr>}
            <tr><td className="ind">Invested</td><td>{out(m.invested)}</td></tr>
            <tr className="total"><td>{m.leftover !== null && m.leftover < 0 ? 'Taken from savings (overspend plus investing)' : 'Left to move'}</td><td>{m.leftover === null ? DASH : naira(Math.abs(m.leftover))}</td></tr>
            {m.otherInflows > 0 && <tr className="muted-row"><td>Other money in (refunds, transfers, redemptions)</td><td>{naira(m.otherInflows)}</td></tr>}
          </tbody>
        </table>
      </section>

      <section className="report-sec">
        <h2>Against the plan</h2>
        <table className="report-table">
          <thead><tr><th></th><th>Actual</th><th>Plan</th><th></th></tr></thead>
          <tbody>
            {rows.map(([label, a, c, up]) => {
              const s = status(a, c, up);
              return <tr key={label}><td>{label}</td><td>{nairaShort(a)}</td><td>{nairaShort(c)}</td><td className={`st ${s.tone}`}>{s.word}</td></tr>;
            })}
          </tbody>
        </table>
      </section>

      <section className="report-sec">
        <h2>Spending by category</h2>
        {spendCats.length ? (
          <table className="report-table">
            <thead><tr><th></th><th>{name.split(' ')[0]}</th><th>Budget</th><th>{prior ? monthLabel(prior.month_start).split(' ')[0] : 'Prior'}</th></tr></thead>
            <tbody>
              {spendCats.map((c) => (
                <tr key={c.category_id}><td>{c.name}</td><td>{naira(c.actual_amount)}</td><td>{c.budget_amount ? naira(c.budget_amount) : DASH}</td>
                  <td>{priorBy.has(c.name) ? naira(priorBy.get(c.name)) : DASH}</td></tr>
              ))}
            </tbody>
          </table>
        ) : <p className="muted">No category totals were recorded.</p>}
      </section>

      {payees.length > 0 && (
        <section className="report-sec">
          <h2>Largest payees</h2>
          <table className="report-table">
            <tbody>{payees.map((p) => <tr key={p.key}><td>{p.key}<small>{p.category ?? ''} · {p.count} payment{p.count === 1 ? '' : 's'}</small></td><td>{naira(p.total)}</td></tr>)}</tbody>
          </table>
        </section>
      )}

      <section className="report-sec report-2">
        <div>
          <h2>Reserve</h2>
          <p className="big">{r.monthsCovered === null ? DASH : `${r.monthsCovered.toFixed(1)} months`}</p>
          <p className="muted">Naira in bank accounts and GT Money Market ({nairaShort(r.liquid)}) against the average personal spend of the last sealed months. Target {RESERVE_TARGET_MONTHS}.</p>
        </div>
        <div>
          <h2>Migrate World</h2>
          {mw ? <>
            <p className="big">{nairaShort(mwMonth?.ngn ?? 0)}</p>
            <p className="muted">This month. {nairaShort(mwToDate)} funded since {mw.summary.since ? monthLabel(`${mw.summary.since}-01`) : DASH}, {nairaShort(mwReceived)} received by month end.</p>
          </> : <p className="muted">Not set up.</p>}
        </div>
      </section>

      <section className="report-sec">
        <h2>Bank balances</h2>
        <table className="report-table">
          <tbody>{banks.map((a) => <tr key={a.id}><td>{a.name}<small>as of {dateLabel(balanceAsOf(a))}</small></td><td>{money(a.current_balance, a.currency)}</td></tr>)}</tbody>
        </table>
        {usd.length > 0 && <p className="muted">Dollar spending this month: {money(usd.reduce((a, t) => a + t.amount, 0), 'USD')}, kept out of the naira totals.</p>}
      </section>

      {(month.monthly_brief || month.next_best_action) && (
        <section className="report-sec">
          <h2>Brief</h2>
          {month.monthly_brief && <p>{month.monthly_brief}</p>}
          {month.next_best_action && <p><b>Next step:</b> {month.next_best_action}</p>}
        </section>
      )}

      <footer className="report-foot">Prepared {dateLabel(new Date().toISOString())} from sealed records. Balances are as last recorded, not live.</footer>
    </article>
  );
}
